"use client";

import * as React from "react";
import { cn } from "../../lib/utils";
import ArthaLinkSmIcon from "../icons/arthalinkSmIcon";
import MenuIcon from "../icons/menuIcon";
import { ProfileAvatar } from "./profile-avatar";

export interface ProfileHeaderProps {
  className?: string;
  name: string;
  handle: string;
  avatarUrl?: string;
  platformIcon?: React.ReactNode;
  onShare?: () => void;
  onNotification?: () => void;
  onAuxiliaryAction?: () => void;
  onMenu?: () => void;
  shareIcon?: React.ReactNode;
  bellIcon?: React.ReactNode;
  auxiliaryIcon?: React.ReactNode;
  menuIcon?: React.ReactNode;
  secondaryActionAriaLabel?: string;
  auxiliaryActionAriaLabel?: string;
}

function ProfileHeader({
  className,
  name,
  handle,
  avatarUrl,
  platformIcon,
  onShare,
  onNotification,
  onAuxiliaryAction,
  onMenu,
  shareIcon,
  bellIcon,
  auxiliaryIcon,
  menuIcon,
  secondaryActionAriaLabel = "Notifications",
  auxiliaryActionAriaLabel = "Additional action",
}: ProfileHeaderProps) {
  return (
    <div className={cn("flex items-center justify-between w-full", className)}>
      {/* Left: Avatar and User Info */}
      <div className="flex items-center gap-3">
        <div className="relative size-[48px]">
          <ProfileAvatar
            src={avatarUrl}
            alt={name}
            className="size-full rounded-full border-2 border-white shadow-sm"
            fallbackClassName="bg-gray-100"
            logoClassName="h-[52%] w-[52%]"
          />
        </div>
        <div className="flex flex-col">
          <span className="text-xl font-semibold text-[#101828] font-phudu">
            {name}
          </span>
          <div className="flex items-center gap-1">
            <span className="text-[#475467]">
              <ArthaLinkSmIcon />
            </span>
            <span className="text-sm text-[#475467] font-medium font-plus-jakarta-sans">
              {handle}
            </span>
          </div>
        </div>
      </div>

      {/* Right: Action Icons */}
      <div className="flex items-center gap-3">
        {onShare && (
          <button
            type="button"
            onClick={onShare}
            className="p-1 hover:bg-gray-100 rounded-md transition-colors text-[#344054]"
            aria-label="Share"
          >
            {shareIcon ?? (
              <svg
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M25 16V20.2C25 21.8802 25 22.7202 24.673 23.362C24.3854 23.9265 23.9265 24.3854 23.362 24.673C22.7202 25 21.8802 25 20.2 25H11.8C10.1198 25 9.27976 25 8.63803 24.673C8.07354 24.3854 7.6146 23.9265 7.32698 23.362C7 22.7202 7 21.8802 7 20.2V16M20 11L16 7M16 7L12 11M16 7V19"
                  stroke="#344054"
                  strokeWidth="1.66667"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>
        )}
        {onNotification && (
          <button
            type="button"
            onClick={onNotification}
            className="rounded-md transition-colors text-[#344054] hover:bg-gray-100"
            aria-label={secondaryActionAriaLabel}
          >
            {bellIcon ?? (
              <svg
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13.3542 25C14.0593 25.6224 14.9855 26 16 26C17.0144 26 17.9407 25.6224 18.6458 25M22 12C22 10.4087 21.3678 8.88258 20.2426 7.75736C19.1174 6.63214 17.5913 6 16 6C14.4087 6 12.8826 6.63214 11.7573 7.75736C10.6321 8.88258 9.99999 10.4087 9.99999 12C9.99999 15.0902 9.22046 17.206 8.34965 18.6054C7.61512 19.7859 7.24785 20.3761 7.26131 20.5408C7.27622 20.7231 7.31485 20.7926 7.46176 20.9016C7.59445 21 8.19258 21 9.38884 21H22.6111C23.8074 21 24.4055 21 24.5382 20.9016C24.6851 20.7926 24.7238 20.7231 24.7387 20.5408C24.7521 20.3761 24.3849 19.7859 23.6503 18.6054C22.7795 17.206 22 15.0902 22 12Z"
                  stroke="#344054"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>
        )}
        {onAuxiliaryAction && (
          <button
            type="button"
            onClick={onAuxiliaryAction}
            className="p-1 hover:bg-gray-100 rounded-md transition-colors text-[#344054]"
            aria-label={auxiliaryActionAriaLabel}
          >
            {auxiliaryIcon}
          </button>
        )}
        {onMenu && (
          <button
            type="button"
            onClick={onMenu}
            className="p-1 hover:bg-gray-100 rounded-md transition-colors text-[#344054]"
            aria-label="Menu"
          >
            {menuIcon ?? <MenuIcon />}
          </button>
        )}
      </div>
    </div>
  );
}

export { ProfileHeader };
